import { FunctionComponent, useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Typography, Box } from '@mui/material';

import useAppDispatch from '../../../../hooks/useAppDispatch';

import { getProjectDetailsFromState } from '../../../../redux/onboarding/selectors';
import { appInstallationCallback } from '../../../../redux/onboarding/service';
import Loader from '../../../../shared/LoaderLinear';

import { Container } from './../../../styled';
import { NavbarUpdated } from '../../../../shared';

import {
    LoadingText,
    ButtonTextBox,
    StyledButton,
    ButtonContentBox,
    ButtonIconBox,
    GithubIconStyled,
    ContentBox,
    WaitingBox,
    LoadingDescription,
    ErrorText,
    SuccessDescription
} from './styled';

type Props = Record<string, never>;

const GithubCallback: FunctionComponent<Props> = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const hasDispatchedCallback = useRef(false);

    const [isHovered, setIsHovered] = useState(false);

    const projectDetails = useSelector(getProjectDetailsFromState);

    useEffect(() => {
        const handleCallback = async () => {
            try {
                if (!hasDispatchedCallback.current) {
                    const url = new URL(window.location.href);
                    const code = url.searchParams.get('code');
                    const installation_id =
                        url.searchParams.get('installation_id');
                    const state = url.searchParams.get('state');

                    if (code && installation_id && state) {
                        hasDispatchedCallback.current = true;

                        await dispatch(
                            appInstallationCallback({
                                code,
                                installation_id,
                                state
                            })
                        );
                    } else {
                        throw new Error('No authorization code found in URL');
                    }
                }
            } catch (err) {
                console.error(err);
            }
        };

        handleCallback();

        return () => {};
    }, [dispatch]);

    useEffect(() => {
        if (projectDetails.success) {
            navigate('/onboarding');
        }
    }, [projectDetails.success, navigate]);

    return (
        <NavbarUpdated>
            <Container>
                <ContentBox>
                    <GithubIconStyled />
                    {projectDetails.loading ? (
                        <>
                            <LoadingText variant="h5">
                                Connecting to GitHub
                            </LoadingText>
                            <LoadingDescription>
                                We're processing your GitHub authorization. This
                                should only take a moment...
                            </LoadingDescription>
                            <Loader />
                            <WaitingBox>
                                <Typography
                                    variant="body2"
                                    sx={{ color: 'white', opacity: 0.7 }}
                                >
                                    Waiting for GitHub response
                                </Typography>
                            </WaitingBox>
                        </>
                    ) : projectDetails?.success ? (
                        <>
                            <LoadingText variant="h5">
                                GitHub Connected Successfully
                            </LoadingText>
                            <SuccessDescription>
                                Redirecting you to continue project setup...
                            </SuccessDescription>
                        </>
                    ) : (
                        <Box sx={{ width: '100%' }}>
                            <ErrorText>
                                We couldn't complete the GitHub authorization
                                process. Please try again or contact support if
                                the issue persists.
                            </ErrorText>
                            <StyledButton
                                variant="contained"
                                fullWidth
                                onClick={() => {
                                    window.location.href =
                                        projectDetails.details.githubAppUrl;
                                }}
                                onMouseEnter={() => setIsHovered(true)}
                                onMouseLeave={() => setIsHovered(false)}
                            >
                                <ButtonContentBox>
                                    <ButtonTextBox $isHovered={isHovered}>
                                        Retry Authorization
                                    </ButtonTextBox>

                                    <ButtonIconBox $isHovered={isHovered}>
                                        🐝
                                    </ButtonIconBox>
                                </ButtonContentBox>
                            </StyledButton>
                        </Box>
                    )}
                </ContentBox>
            </Container>
        </NavbarUpdated>
    );
};

export default GithubCallback;
